import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Breadcrump from "../../components/Breadcrump";
import categoryAPI from "../../api/categoryApi";
import CategoryCard from "./Card";

gsap.registerPlugin(ScrollTrigger);

// ─── Skeleton Grid ────────────────────────────────────────────────────────────
function SkeletonGrid({ count = 6 }) {
  return (
    <div className="catlist-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`catlist-skeleton ${
            i % 7 === 0 ? "catlist-grid-item--featured" : ""
          }`}
          style={{
            minHeight: i % 7 === 0 ? "680px" : "320px",
          }}
        />
      ))}
    </div>
  );
}

// ─── List Page ───────────────────────────────────────────────────────────────
function ListPage() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // GSAP refs
  const heroRef = useRef(null);
  const gridRef = useRef(null);
  const cardRefs = useRef([]);
  cardRefs.current = [];

  const addCardRef = (el) => {
    if (el && !cardRefs.current.includes(el)) {
      cardRefs.current.push(el);
    }
  };

  // ── Fetch ──
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await categoryAPI.listing();

      if (res.code === 200) {
        // const { items: data, totalCount: pagination } = res.data;
        const { items: data, pagination } = res.data;
        setItems(data || []);
        setTotalCount(pagination.totalCount || 0);
      } else {
        setError("Failed to load categories.");
      }
    } catch (err) {
      console.error("Error loading categories:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── GSAP Animations ──
  useEffect(() => {
    if (loading || items.length === 0) return;
    if (typeof gsap === "undefined") return;

    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.from(heroRef.current, {
        opacity: 0,
        y: 20,
        duration: 0.7,
        ease: "power3.out",
      });

      // Cards stagger
      if (cardRefs.current.length > 0) {
        gsap.from(cardRefs.current, {
          opacity: 0,
          y: 30,
          duration: 0.7,
          stagger: 0.08,
          ease: "power3.out",
          scrollTrigger: {
            trigger: gridRef.current,
            start: "top 80%",
            once: true,
          },
        });
      }
    });

    return () => ctx.revert();
  }, [loading, items.length]);

  // ── Determine which indices should be "featured" ──
  // Every 7th item (0, 7, 14...) gets the featured (tall) treatment
  const isFeaturedIndex = (index) => index % 7 === 0;

  return (
    <>
      {/* Hero */}
      <Breadcrump title="Category" />
      <div className="catlist-hero" ref={heroRef}>
        <div className="container pt-4">
          {/* <p className="catlist-eyebrow">
            <span className="catlist-eyebrow-line" aria-hidden="true" />
            Our Categories
          </p> */}
          <h1 className="catlist-hero-heading">
            Shop by <em>Category</em>
          </h1>
          {!loading && (
            <p className="catlist-count mb-0">
              {totalCount} {totalCount === 1 ? "category" : "categories"}{" "}
              available
            </p>
          )}
        </div>
      </div>

      {/* Grid */}
      <section className="catlist-section">
        <div className="container">
          {loading && <SkeletonGrid count={6} />}

          {!loading && error && (
            <div className="catlist-empty">
              <div className="catlist-empty-icon">⚠️</div>
              <h3>Could not load categories</h3>
              <p>{error}</p>
              <button
                onClick={fetchCategories}
                className="btn btn-outline-secondary mt-3"
                style={{ letterSpacing: "0.1em", fontSize: "0.8rem" }}
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div className="catlist-empty">
              <div className="catlist-empty-icon">🪡</div>
              <h3>No collections yet</h3>
              <p>Check back soon for our curated categories.</p>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <div className="catlist-grid" ref={gridRef}>
              {items.map((item, index) => (
                <div
                  key={item._id || item.id || index}
                  className={
                    isFeaturedIndex(index) ? "catlist-grid-item--featured" : ""
                  }
                >
                  <CategoryCard
                    item={item}
                    featured={isFeaturedIndex(index)}
                    cardRef={addCardRef}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default ListPage;
